import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CircleCheck, CircleX } from "lucide-react";
import PaperlessEobResultDialog from "./PaperlessEobResultDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { QueryKeys } from "@/constants";
import { queryClient } from "@/hooks/use-query-client";
import useUserInfo from "@/hooks/use-user-info";
import { lowerCaseComparison } from "@/lib/string-util";
import type {
  PaperlessEobFetchResponse,
  PaperlessEobUpdateRequest,
} from "@/model/paperlessEob";
import { PAPARLESS_EOB_UPDATE_ACTION } from "@/model/paperlessEob";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { updatePaperlessEob } from "@/services/paperlessEob";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import IDENTIFIERS from "@/constants/test_id";
import { AuthMode } from "@/context/auth-provider";
import { MANAGE_EOB_MESSAGE } from "@/constants/manage-eob";
import { isCoverageActive } from "@/lib/utils";

export type DialogProps = {
  dialogOpen: boolean;
  setDialogOpen: (dialogOpen: boolean) => void;
  logDialogName: string | undefined;
  logTrigger: string | undefined;
};

const ManageEob = ({
  dialogOpen,
  setDialogOpen,
  logDialogName,
  logTrigger,
}: DialogProps) => {
  const userInfo = useUserInfo();
  const fetchEobResponse = queryClient.getQueryData<PaperlessEobFetchResponse>([
    QueryKeys.PAPERLESS_EOB_FETCH,
  ]);
  const eobPreference = fetchEobResponse?.data?.preference;
  const isGuest = lowerCaseComparison(userInfo.mode, AuthMode.GUEST);
  const isMemberPlanActive = userInfo.planCoverageInfo.some((item) =>
    isCoverageActive(item),
  );

  const initialMailChecked = lowerCaseComparison(
    eobPreference,
    PAPARLESS_EOB_UPDATE_ACTION.MAIL,
  );
  const [mailChecked, setMailChecked] = useState<boolean>(initialMailChecked);

  const hasChanged = mailChecked !== initialMailChecked;
  const action = mailChecked
    ? PAPARLESS_EOB_UPDATE_ACTION.MAIL
    : PAPARLESS_EOB_UPDATE_ACTION.PAPERLESS;

  const updateMutation = useMutation({
    mutationFn: updatePaperlessEob,
    onSuccess: (res: PaperlessEobFetchResponse) => {
      queryClient.setQueryData<PaperlessEobFetchResponse>(
        [QueryKeys.PAPERLESS_EOB_FETCH],
        (prev) =>
          prev
            ? {
                ...prev,
                data: { ...prev.data, preference: res?.data?.preference },
              }
            : res,
      );
    },
  });

  const showResult = updateMutation.isSuccess || updateMutation.isError;

  function onSave() {
    const updateReqData: PaperlessEobUpdateRequest = {
      groupId: userInfo.groupId || "",
      memberId: userInfo.memberId || "",
      accessToken: userInfo.accessToken || "",
      action,
    };

    updateMutation.mutate(updateReqData);
  }

  function onClose() {
    updateMutation.reset();
    setDialogOpen(false);
  }

  if (showResult) {
    const isSuccess = updateMutation.isSuccess;
    const resMessage = updateMutation.data?.data?.message;
    const resultMessage = {
      title: isSuccess
        ? MANAGE_EOB_MESSAGE.successStatus
        : MANAGE_EOB_MESSAGE.failureStatus,
      body: isSuccess
        ? resMessage ||
          (mailChecked
            ? MANAGE_EOB_MESSAGE.physicalStatusMessage
            : MANAGE_EOB_MESSAGE.virtualStatusMessage)
        : resMessage || MANAGE_EOB_MESSAGE.errorStatusMessage,
      confirmBtnLabel: MANAGE_EOB_MESSAGE.acknowledgeButtonLabel,
      confirmBtnStatus: true,
      icon: isSuccess ? CircleCheck : CircleX,
      iconClassName: isSuccess
        ? "text-blue-400 bg-blue-100"
        : "text-red-700 bg-red-100",
    };

    return (
      <AlertDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        logDialogName={logDialogName}
        logCategory={LogCategories.PAPERLESS_EOB}
        logSubCategory={LogSubCategories.MANAGE_EOB}
        logTag={LogTags.MODULE_ACCESS}
        logTrigger={logTrigger}
      >
        <PaperlessEobResultDialog
          message={resultMessage}
          onClose={onClose}
          testId={IDENTIFIERS.MANAGE_EOB.ACKNOWLEDGE_BUTTON}
        />
      </AlertDialog>
    );
  }

  return (
    <AlertDialog
      open={dialogOpen}
      onOpenChange={(open) => setDialogOpen(open)}
      logDialogName={logDialogName}
      logCategory={LogCategories.PAPERLESS_EOB}
      logSubCategory={LogSubCategories.MANAGE_EOB}
      logTag={LogTags.MODULE_ACCESS}
      logTrigger={logTrigger}
    >
      <AlertDialogContent className="m-auto flex min-h-[200px] max-w-md flex-col justify-between gap-8 rounded-lg sm:max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex flex-col items-center gap-1 text-lg font-medium">
            {MANAGE_EOB_MESSAGE.title}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription asChild>
          <div className="flex flex-col gap-4 text-[0.875rem]">
            <div className="flex items-center gap-4">
              <Checkbox
                id="e-eob"
                checked={mailChecked}
                onCheckedChange={(checked) => setMailChecked(checked === true)}
              />
              <Label htmlFor="e-eob" className="text-[0.875rem] font-normal">
                {MANAGE_EOB_MESSAGE.physicalEobCheckboxDescription}
              </Label>
            </div>
            <span>{MANAGE_EOB_MESSAGE.defaultDescription}</span>
          </div>
        </AlertDialogDescription>

        <AlertDialogFooter className="mt-2 flex w-full flex-row items-end justify-end gap-4 rounded-lg px-2">
          <Button
            variant="outline"
            type="button"
            className="max-w-36 text-gray-700 outline-none hover:font-semibold focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => {
              setMailChecked(initialMailChecked);
              setDialogOpen(false);
            }}
            disabled={updateMutation.isPending}
            data-testid={IDENTIFIERS.MANAGE_EOB.CANCEL_BUTTON}
          >
            {MANAGE_EOB_MESSAGE.negativeButtonLabel}
          </Button>

          <Button
            variant="default"
            type="button"
            className="max-w-36 outline-none hover:bg-primary-dark focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            disabled={
              !hasChanged ||
              updateMutation.isPending ||
              isGuest ||
              !isMemberPlanActive
            }
            onClick={onSave}
            data-testid={IDENTIFIERS.MANAGE_EOB.SAVE_BUTTON}
            logMetadata={{
              action,
            }}
          >
            {MANAGE_EOB_MESSAGE.positiveButtonLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ManageEob;
